import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtGHS, fmtDate, fmtDateTime, pName } from "@/lib/format";
import { esc } from "@/lib/print";
import { Plus, Trash2, ClipboardList, Truck, PackageCheck, Sparkles, Pencil } from "lucide-react";
import { ScanBox } from "@/components/ScanBox";
import { ProductCombobox } from "@/components/ProductCombobox";
import { PackEntry } from "@/components/PackEntry";
import { toast } from "sonner";

const poStatusValue = (s) => ({ draft: "requested", ordered: "dispatched", partial: "dispatched", received: "received", cancelled: "out", closed: "received" }[s] || "requested");

export default function Procurement() {
  const { can } = useAuth();
  const { branches } = useBranch();
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ supplier_id: "", branch_id: "", expected_date: "", notes: "", lines: [] });
  const [sel, setSel] = useState(null);
  const [grn, setGrn] = useState(null); // { po, lines: [...] }
  const [grnHighlight, setGrnHighlight] = useState(null);
  const [scanCounts, setScanCounts] = useState({});
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoBranch, setAutoBranch] = useState("");

  const load = () => api.get("/purchase-orders").then((r) => setPos(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { product_id: "", quantity: 1, unit_cost: 0 }] }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const packPatch = ({ quantity, unitCost }) => ({ ...(quantity !== undefined && { quantity }), ...(unitCost !== undefined && { unit_cost: unitCost }) });
  const applyPack = (i, v) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...packPatch(v) } : l) }));
  const applyGrnPack = (i, v) => setGrn((g) => ({ ...g, lines: g.lines.map((l, idx) => idx === i ? { ...l, ...packPatch(v) } : l) }));
  const prodById = (id) => products.find((p) => p.id === id);
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, product_id: pid, unit_cost: p?.cost_price ?? 0 } : l) }));
  };

  const openNew = () => { setEditingId(null); setForm({ supplier_id: "", branch_id: branches[0]?.id || "", expected_date: "", notes: "", lines: [{ product_id: "", quantity: 1, unit_cost: 0 }] }); setOpen(true); };
  const openEdit = (po) => {
    setEditingId(po.id);
    setForm({ supplier_id: po.supplier_id, branch_id: po.branch_id, expected_date: po.expected_date || "", notes: po.notes || "",
      lines: po.lines.map((l) => ({ product_id: l.product_id, quantity: l.quantity, unit_cost: l.unit_cost })) });
    setOpen(true);
  };
  const submitPO = async () => {
    const payload = {
      supplier_id: form.supplier_id, branch_id: form.branch_id, expected_date: form.expected_date, notes: form.notes,
      lines: form.lines.filter((l) => l.product_id).map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) })),
    };
    try {
      if (editingId) { await api.put(`/purchase-orders/${editingId}`, payload); toast.success("Purchase order updated"); }
      else { await api.post("/purchase-orders", payload); toast.success("Purchase order created"); }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (po, status) => {
    try { await api.post(`/purchase-orders/${po.id}/status?status=${status}`); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const closePO = async (po) => {
    try { await api.post(`/purchase-orders/${po.id}/close`); toast.success("Procurement closed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const suggest = async () => {
    if (!form.branch_id) return toast.error("Select a destination location first");
    try {
      const { data } = await api.get("/purchase-order-suggestions", { params: { branch_id: form.branch_id } });
      let lines = data.lines;
      if (form.supplier_id) lines = lines.filter((l) => l.supplier_id === form.supplier_id);
      if (!lines.length) return toast(form.supplier_id ? "No low-stock items for this supplier at this location" : "No low-stock items at this location (only products it has stocked before are checked)");
      const MAX = 150;
      const total = lines.length;
      lines = lines.slice(0, MAX);
      setForm((f) => ({ ...f, lines: lines.map((l) => ({ product_id: l.product_id, quantity: l.suggested_qty, unit_cost: l.unit_cost })) }));
      toast.success(`Suggested ${lines.length} item(s) from low stock`);
      if (total > MAX) toast.warning(`${total - MAX} more low-stock item(s) not added — pick a supplier first to narrow the list`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const openAuto = () => { setAutoBranch(branches[0]?.id || ""); setAutoOpen(true); };
  const autoGenerate = async () => {
    if (!autoBranch) return toast.error("Select a destination location");
    try {
      const { data } = await api.post(`/purchase-orders/auto-generate?branch_id=${autoBranch}`);
      if (!data.created.length) {
        toast(data.skipped.length ? `No draft POs created — ${data.skipped.length} low-stock item(s) have no preferred supplier` : "No low-stock items to reorder");
      } else {
        toast.success(`Created ${data.created.length} draft PO(s)${data.skipped.length ? ` · ${data.skipped.length} item(s) skipped (no preferred supplier)` : ""}`);
      }
      setAutoOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const printPO = (po) => {
    const rows = po.lines.map((l) => `<tr><td>${esc(l.generic_name)}</td><td>${esc(l.sku)}</td><td style="text-align:right">${l.quantity}</td><td style="text-align:right">GHS ${Number(l.unit_cost).toFixed(2)}</td><td style="text-align:right">GHS ${Number(l.line_total || l.quantity * l.unit_cost).toFixed(2)}</td></tr>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`<html><head><title>${esc(po.po_number)}</title><style>body{font-family:Arial,sans-serif;padding:32px;color:#142C3B}h1{font-size:20px;margin:0}table{width:100%;border-collapse:collapse;margin-top:16px}th,td{border:1px solid #ccc;padding:8px;font-size:13px}th{background:#eef4f8;text-align:left}</style></head><body>
      <h1>Purchase Order — ${po.po_number}</h1>
      <p style="color:#526675;font-size:12px;margin:4px 0 16px">MobiMed Pharmacy · Ghana</p>
      <p><b>Supplier:</b> ${esc(po.supplier_name)}<br/><b>Deliver to:</b> ${esc(branchName(po.branch_id))}<br/><b>Expected:</b> ${esc(po.expected_date || "—")}<br/><b>Date:</b> ${new Date(po.created_at).toLocaleDateString()}</p>
      <table><thead><tr><th>Product</th><th>SKU</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit cost</th><th style="text-align:right">Total</th></tr></thead><tbody>${rows}</tbody></table>
      <h3 style="text-align:right;margin-top:16px">Total: GHS ${Number(po.total).toFixed(2)}</h3>
      ${po.notes ? `<p><b>Notes:</b> ${esc(po.notes)}</p>` : ""}
      <p style="margin-top:48px">Authorised by: ____________________________</p>
      </body></html>`);
    w.document.close(); w.focus(); w.print();
  };

  const openGRN = (po) => {
    const draft = po.grn_draft || [];
    const byId = Object.fromEntries(draft.map((d) => [d.product_id, d]));
    setGrn({ po, lines: po.lines.map((l) => {
      const d = byId[l.product_id] || {};
      return { product_id: l.product_id, generic_name: l.generic_name, sku: l.sku,
        batch_no: d.batch_no || "", expiry_date: d.expiry_date || "",
        quantity: d.quantity != null ? d.quantity : l.quantity - (l.received_qty || 0),
        unit_cost: d.unit_cost != null ? d.unit_cost : l.unit_cost }; }) });
    setGrnHighlight(null);
    setScanCounts({});
  };
  const setGrnLine = (i, k, v) => setGrn((g) => ({ ...g, lines: g.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const handleGrnScan = async (code) => {
    let pid = null;
    try { const { data } = await api.get("/products", { params: { barcode: code } }); pid = (data || [])[0]?.id; } catch { /* ignore */ }
    if (!pid) { toast.error(`No product matches barcode ${code}`); return; }
    const idx = grn.lines.findIndex((l) => l.product_id === pid);
    if (idx < 0) { toast.error("That product is not on this purchase order"); return; }
    const name = grn.lines[idx].generic_name;
    const poLine = (grn.po.lines || []).find((pl) => pl.product_id === pid) || {};
    const outstanding = Math.max(0, (poLine.quantity || 0) - (poLine.received_qty || 0));
    const current = scanCounts[pid] ?? 0;
    setGrnHighlight(pid);
    if (outstanding && current >= outstanding) {
      setGrnLine(idx, "quantity", outstanding);
      toast.warning(`${name}: all ${outstanding} already scanned`);
    } else {
      const next = current + 1;
      setScanCounts((s) => ({ ...s, [pid]: next }));
      setGrnLine(idx, "quantity", next);
      toast.success(`${name} — ${next}${outstanding ? ` of ${outstanding}` : ""} scanned`);
    }
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="grn-batch-${idx}"]`);
      if (el) { el.scrollIntoView({ block: "center", behavior: "smooth" }); el.focus(); }
    }, 60);
  };
  // Short-dated = less than 9 months to expiry from today.
  const monthsToExpiry = (d) => { if (!d) return null; return (new Date(d) - new Date()) / (1000 * 60 * 60 * 24 * 30.4375); };
  const isShortDated = (l) => { const m = monthsToExpiry(l.expiry_date); return m != null && m < 9; };
  const saveGRNDraft = async () => {
    const lines = grn.lines.map((l) => ({ product_id: l.product_id, batch_no: l.batch_no, expiry_date: l.expiry_date, quantity: Number(l.quantity) || 0, unit_cost: Number(l.unit_cost) || 0 }));
    try { await api.post(`/purchase-orders/${grn.po.id}/grn-draft`, { lines }); toast.success("Progress saved — resume anytime before receiving"); setGrn(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submitGRN = async () => {
    const active = grn.lines.filter((l) => Number(l.quantity) > 0 && l.batch_no && l.expiry_date);
    const blocked = active.filter((l) => isShortDated(l) && !l.short_dated_ok);
    if (blocked.length) return toast.error(`${blocked.length} line(s) expire in under 9 months — tick "Receive short-dated" to override`);
    const lines = active.map((l) => ({ product_id: l.product_id, batch_no: l.batch_no, expiry_date: l.expiry_date, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) }));
    if (!lines.length) return toast.error("Enter batch no., expiry and quantity for at least one line");
    try { await api.post(`/purchase-orders/${grn.po.id}/receive`, { lines }); toast.success("Goods received into stock"); setGrn(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <div>
      <PageHeader title="Purchase Orders" subtitle="Central procurement — order from suppliers and receive goods into a location"
        actions={can("procurement.manage") && (
          <div className="flex items-center gap-2">
            <Button variant="outline" data-testid="auto-po-btn" onClick={openAuto}><Sparkles className="h-4 w-4 mr-1.5" /> Auto-generate by supplier</Button>
            <Button data-testid="new-po-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> New Purchase Order</Button>
          </div>
        )} />

      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">PO No.</th><th className="py-2.5 px-3">Supplier</th><th className="py-2.5 px-3">Destination</th>
            <th className="py-2.5 px-3 text-right">Lines</th><th className="py-2.5 px-3 text-right">Total</th><th className="py-2.5 px-3">Status</th>
            <th className="py-2.5 px-3">Created</th><th className="py-2.5 px-3"></th></tr>
          </thead>
          <tbody>
            {pos.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground"><ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-40" />No purchase orders</td></tr>}
            {pos.map((p) => (
              <tr key={p.id} className="border-t border-border hover:bg-muted/50">
                <td className="py-2 px-3 font-mono text-xs text-sky-700 cursor-pointer" onClick={() => setSel(p)}>{p.po_number}</td>
                <td className="py-2 px-3 text-foreground">{p.supplier_name}{p.auto && <span data-testid={`auto-badge-${p.po_number}`} className="ml-2 align-middle text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">Auto · review</span>}</td>
                <td className="py-2 px-3 text-muted-foreground">{p.branch_name}</td>
                <td className="py-2 px-3 text-right font-mono">{p.lines.length}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(p.total)}</td>
                <td className="py-2 px-3"><StatusBadge value={poStatusValue(p.status)} label={p.status} /></td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(p.created_at)}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  {can("procurement.manage") && (p.status === "ordered" || p.status === "partial") && p.grn_draft && <span data-testid={`grn-draft-${p.po_number}`} className="mr-1 align-middle text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">receipt in progress</span>}
                  {can("procurement.manage") && p.status === "draft" && <Button size="sm" variant="ghost" data-testid={`edit-po-${p.po_number}`} onClick={() => openEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>}
                  {can("procurement.manage") && p.status === "draft" && <Button size="sm" variant="outline" data-testid={`order-po-${p.po_number}`} onClick={() => setStatus(p, "ordered")}><Truck className="h-3.5 w-3.5 mr-1" /> Mark ordered</Button>}
                  {can("procurement.manage") && (p.status === "ordered" || p.status === "partial") && <Button size="sm" data-testid={`receive-po-${p.po_number}`} onClick={() => openGRN(p)}><PackageCheck className="h-3.5 w-3.5 mr-1" /> Receive</Button>}
                  {can("procurement.manage") && (p.status === "received" || p.status === "partial") && <Button size="sm" variant="outline" data-testid={`close-po-${p.po_number}`} onClick={() => closePO(p)}>Close</Button>}
                  <Button size="sm" variant="ghost" data-testid={`print-po-${p.po_number}`} onClick={() => printPO(p)}>Print</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Create PO */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditingId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto [&>*]:min-w-0">
          <DialogHeader><DialogTitle>{editingId ? "Edit Purchase Order" : "New Purchase Order"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Supplier</Label>
                <Select value={form.supplier_id} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
                  <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Deliver to</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm((f) => ({ ...f, branch_id: v }))}>
                  <SelectTrigger data-testid="po-branch-select"><SelectValue placeholder="Location" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Expected date</Label><Input type="date" data-testid="po-expected-input" value={form.expected_date} onChange={(e) => setForm((f) => ({ ...f, expected_date: e.target.value }))} /></div>
            <div>
              <div className="flex flex-wrap items-center justify-between gap-1 mb-1"><Label>Items</Label>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" data-testid="po-suggest-btn" onClick={suggest} disabled={!form.branch_id}>Suggest from low stock</Button>
                  <Button size="sm" variant="ghost" onClick={addLine}><Plus className="h-3.5 w-3.5 mr-1" /> Add line</Button>
                </div>
              </div>
              <div className="space-y-2">
                {form.lines.map((l, i) => (
                  <div key={i} className="space-y-1">
                  <div className="flex flex-wrap sm:flex-nowrap gap-2 items-end">
                    <div className="basis-full sm:basis-auto sm:flex-1 min-w-0">
                      <ProductCombobox products={products} value={l.product_id} onChange={(v) => pickProduct(i, v)} testId={`po-product-${i}`} />
                    </div>
                    <div className="w-20"><Label className="text-[10px]">Qty</Label><Input type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`po-qty-${i}`} /></div>
                    <div className="w-24"><Label className="text-[10px]">Unit cost</Label><Input type="number" step="0.01" value={l.unit_cost} onChange={(e) => setLine(i, "unit_cost", e.target.value)} data-testid={`po-cost-${i}`} /></div>
                    <Button size="icon" variant="ghost" onClick={() => rmLine(i)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                    <PackEntry product={prodById(l.product_id)} onApply={(v) => applyPack(i, v)} testId={`po-pack-${i}`} quantity={l.quantity} unitCost={l.unit_cost} />
                  </div>
                ))}
              </div>
              <div className="text-right text-sm font-mono mt-2 text-muted-foreground">Total: {fmtGHS(form.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0), 0))}</div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="submit-po-btn" onClick={submitPO}>{editingId ? "Save changes" : "Create PO"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PO detail */}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{sel?.po_number}</DialogTitle></DialogHeader>
          {sel && (
            <div className="text-sm">
              <div className="flex justify-between text-muted-foreground mb-3"><span>{sel.supplier_name} → {branchName(sel.branch_id)}</span><StatusBadge value={poStatusValue(sel.status)} label={sel.status} /></div>
              {sel.expected_date && <div className="text-xs text-muted-foreground mb-2">Expected: {fmtDate(sel.expected_date)}</div>}
              <div className="divide-y divide-border border-y border-border">
                {sel.lines.map((l, i) => (
                  <div key={i} className="py-2 flex justify-between">
                    <div><div className="font-medium">{l.generic_name}</div><div className="text-xs text-muted-foreground">received {l.received_qty || 0} / {l.quantity}</div></div>
                    <span className="font-mono">{l.quantity} × {fmtGHS(l.unit_cost)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-semibold pt-2"><span>Total</span><span className="font-mono">{fmtGHS(sel.total)}</span></div>
            </div>
          )}
          {sel && (
            <DialogFooter>
              <Button variant="outline" data-testid="print-po-btn" onClick={() => printPO(sel)}>Print / Send</Button>
              {sel.status === "draft" && can("procurement.manage") && <Button data-testid="edit-po-detail-btn" onClick={() => { openEdit(sel); setSel(null); }}>Edit</Button>}
              {(sel.status === "received" || sel.status === "partial") && can("procurement.manage") && <Button data-testid="close-po-detail-btn" onClick={() => { closePO(sel); setSel(null); }}>Close procurement</Button>}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* GRN receive */}
      <Dialog open={!!grn} onOpenChange={(o) => { if (!o) { setGrn(null); setGrnHighlight(null); setScanCounts({}); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Receive goods · {grn?.po?.po_number}</DialogTitle></DialogHeader>
          {grn && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Scan each pack to tally its received quantity (or type it in), then enter batch number and expiry. Received stock enters {branchName(grn.po.branch_id)} and updates the product's cost.</p>
              <ScanBox onScan={handleGrnScan} placeholder="Scan a pack to count it as received…" />
              {grn.lines.map((l, i) => (
                <div key={i} className={`rounded-lg border p-3 transition-colors ${grnHighlight === l.product_id ? "border-emerald-500 ring-2 ring-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-500/5" : "border-border"}`}>
                  <div className="font-medium text-sm mb-2 flex items-center gap-2">{l.generic_name} <span className="text-muted-foreground font-mono text-xs">{l.sku}</span>{scanCounts[l.product_id] ? <span className="text-[10px] rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" data-testid={`grn-scanned-badge-${i}`}>scanned ×{scanCounts[l.product_id]}</span> : null}</div>
                  <div className="grid grid-cols-4 gap-2">
                    <div><Label className="text-[10px]">Batch no.</Label><Input data-testid={`grn-batch-${i}`} value={l.batch_no} onChange={(e) => setGrnLine(i, "batch_no", e.target.value)} /></div>
                    <div><Label className="text-[10px]">Expiry</Label><Input type="date" data-testid={`grn-expiry-${i}`} value={l.expiry_date} onChange={(e) => setGrnLine(i, "expiry_date", e.target.value)} className={isShortDated(l) ? "border-amber-500 focus-visible:ring-amber-500" : ""} /></div>
                    <div><Label className="text-[10px]">Qty</Label><Input type="number" data-testid={`grn-qty-${i}`} value={l.quantity} onChange={(e) => setGrnLine(i, "quantity", e.target.value)} /></div>
                    <div><Label className="text-[10px]">Unit cost</Label><Input type="number" step="0.01" data-testid={`grn-cost-${i}`} value={l.unit_cost} onChange={(e) => setGrnLine(i, "unit_cost", e.target.value)} /></div>
                  </div>
                  <div className="mt-2"><PackEntry product={prodById(l.product_id)} onApply={(v) => applyGrnPack(i, v)} testId={`grn-pack-${i}`} quantity={l.quantity} unitCost={l.unit_cost} /></div>
                  {isShortDated(l) && (
                    <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 px-2.5 py-1.5" data-testid={`grn-shortdated-warn-${i}`}>
                      <span className="text-xs text-amber-700 dark:text-amber-400">⚠ Expires in under 9 months — short-dated stock</span>
                      <label className="flex items-center gap-1.5 text-xs font-medium text-amber-800 dark:text-amber-300 cursor-pointer whitespace-nowrap">
                        <input type="checkbox" data-testid={`grn-shortdated-override-${i}`} checked={!!l.short_dated_ok} onChange={(e) => setGrnLine(i, "short_dated_ok", e.target.checked)} /> Receive short-dated
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setGrn(null)}>Cancel</Button><Button variant="outline" data-testid="save-grn-draft-btn" onClick={saveGRNDraft}>Save for later</Button><Button data-testid="submit-grn-btn" onClick={submitGRN}>Receive into stock</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Auto-generate POs by supplier */}
      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Auto-generate purchase orders</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Scans the chosen location for items at or below their reorder level and creates one draft PO per preferred supplier. Items without a preferred supplier are skipped.</p>
            <div><Label>Destination location</Label>
              <Select value={autoBranch} onValueChange={setAutoBranch}>
                <SelectTrigger data-testid="auto-branch-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoOpen(false)}>Cancel</Button>
            <Button data-testid="submit-auto-po-btn" onClick={autoGenerate}>Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
